import { parseCivilDate } from './dates';

export const RECURRENCE_UNITS = Object.freeze({ WEEK: 'week', MONTH: 'month', YEAR: 'year' });
export const RECURRENCE_INVALID_DAY_POLICIES = Object.freeze({ CLAMP: 'clamp' });
export const RECURRENCE_ERROR_CODES = Object.freeze({
  INVALID_RULE: 'INVALID_RECURRENCE_RULE',
  INVALID_RANGE: 'INVALID_RECURRENCE_RANGE',
  INVALID_OCCURRENCE_IDENTITY: 'INVALID_OCCURRENCE_IDENTITY',
  ITERATION_LIMIT_EXCEEDED: 'RECURRENCE_ITERATION_LIMIT_EXCEEDED',
});
export const MAX_RECURRENCE_ITERATIONS = 10000;

const SERIES_ID_PATTERN = /^[A-Za-z0-9_-]+$/;
const MAX_SERIES_ID_LENGTH = 128;
const MIN_YEAR = 0;
const MAX_YEAR = 9999;

export class RecurrenceDomainError extends Error {
  constructor(code, details = []) {
    super(code);
    this.name = 'RecurrenceDomainError';
    this.code = code;
    this.details = details;
  }
}

const isLeapYear = (year) => year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
const getDaysInMonth = (year, month) => {
  if (month === 2) return isLeapYear(year) ? 29 : 28;
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
};
const pad = (value) => String(value).padStart(2, '0');
const formatCivilParts = ({ year, month, day }) => (
  `${String(year).padStart(4, '0')}-${pad(month)}-${pad(day)}`
);

// Calendário gregoriano proléptico; o ordinal interno não representa UTC.
const civilToOrdinal = ({ year: originalYear, month, day }) => {
  const year = originalYear - (month <= 2 ? 1 : 0);
  const era = Math.floor(year / 400);
  const yearOfEra = year - era * 400;
  const shiftedMonth = month + (month > 2 ? -3 : 9);
  const dayOfYear = Math.floor((153 * shiftedMonth + 2) / 5) + day - 1;
  const dayOfEra = yearOfEra * 365
    + Math.floor(yearOfEra / 4)
    - Math.floor(yearOfEra / 100)
    + dayOfYear;
  return era * 146097 + dayOfEra;
};

const ordinalToCivil = (ordinal) => {
  const era = Math.floor(ordinal / 146097);
  const dayOfEra = ordinal - era * 146097;
  const yearOfEra = Math.floor(
    (dayOfEra - Math.floor(dayOfEra / 1460) + Math.floor(dayOfEra / 36524) - Math.floor(dayOfEra / 146096)) / 365
  );
  let year = yearOfEra + era * 400;
  const dayOfYear = dayOfEra - (
    365 * yearOfEra + Math.floor(yearOfEra / 4) - Math.floor(yearOfEra / 100)
  );
  const monthPrime = Math.floor((5 * dayOfYear + 2) / 153);
  const day = dayOfYear - Math.floor((153 * monthPrime + 2) / 5) + 1;
  const month = monthPrime + (monthPrime < 10 ? 3 : -9);
  year += month <= 2 ? 1 : 0;
  return { year, month, day };
};

export const shiftCivilDays = (date, days) => {
  const parsed = parseCivilDate(date);
  if (!parsed || !Number.isSafeInteger(days)) return null;
  const result = ordinalToCivil(civilToOrdinal(parsed) + days);
  if (result.year < MIN_YEAR || result.year > MAX_YEAR) return null;
  return formatCivilParts(result);
};

const isValidSeriesId = (seriesId) => (
  typeof seriesId === 'string'
  && seriesId.length > 0
  && seriesId.length <= MAX_SERIES_ID_LENGTH
  && SERIES_ID_PATTERN.test(seriesId)
);

const getRuleErrors = (rule) => {
  if (!rule || typeof rule !== 'object' || Array.isArray(rule)) return ['rule'];
  const errors = [];
  if (!isValidSeriesId(rule.seriesId)) errors.push('seriesId');
  if (!Object.values(RECURRENCE_UNITS).includes(rule.unit)) errors.push('unit');
  if (!Number.isSafeInteger(rule.interval) || rule.interval < 1) errors.push('interval');
  if (!parseCivilDate(rule.anchorDate)) errors.push('anchorDate');
  if (rule.invalidDayPolicy !== RECURRENCE_INVALID_DAY_POLICIES.CLAMP) errors.push('invalidDayPolicy');

  ['untilDate', 'effectiveFromDate', 'effectiveUntilDate'].forEach((field) => {
    if (rule[field] !== undefined && rule[field] !== null && !parseCivilDate(rule[field])) errors.push(field);
  });
  if (parseCivilDate(rule.anchorDate) && parseCivilDate(rule.untilDate) && rule.untilDate < rule.anchorDate) {
    errors.push('untilDateBeforeAnchorDate');
  }
  if (
    parseCivilDate(rule.effectiveFromDate)
    && parseCivilDate(rule.effectiveUntilDate)
    && rule.effectiveUntilDate < rule.effectiveFromDate
  ) errors.push('effectiveRange');
  return errors;
};

export const validateRecurrenceRule = (rule) => {
  const errors = getRuleErrors(rule);
  return { valid: errors.length === 0, errors };
};
export const isValidRecurrenceRule = (rule) => validateRecurrenceRule(rule).valid;

const assertValidRule = (rule) => {
  const validation = validateRecurrenceRule(rule);
  if (!validation.valid) {
    throw new RecurrenceDomainError(RECURRENCE_ERROR_CODES.INVALID_RULE, validation.errors);
  }
};

const getOccurrenceAtIndex = (rule, index) => {
  if (!Number.isSafeInteger(index) || index < 0) return null;
  const anchor = parseCivilDate(rule.anchorDate);
  if (rule.unit === RECURRENCE_UNITS.WEEK) {
    const days = index * rule.interval * 7;
    return Number.isSafeInteger(days) ? shiftCivilDays(rule.anchorDate, days) : null;
  }
  if (rule.unit === RECURRENCE_UNITS.MONTH) {
    const targetMonthIndex = anchor.year * 12 + anchor.month - 1 + index * rule.interval;
    if (!Number.isSafeInteger(targetMonthIndex)) return null;
    const year = Math.floor(targetMonthIndex / 12);
    const month = targetMonthIndex - year * 12 + 1;
    if (year < MIN_YEAR || year > MAX_YEAR) return null;
    return formatCivilParts({ year, month, day: Math.min(anchor.day, getDaysInMonth(year, month)) });
  }
  const year = anchor.year + index * rule.interval;
  if (!Number.isSafeInteger(year) || year < MIN_YEAR || year > MAX_YEAR) return null;
  return formatCivilParts({
    year,
    month: anchor.month,
    day: Math.min(anchor.day, getDaysInMonth(year, anchor.month)),
  });
};

const getFirstIndexOnOrAfter = (rule, lowerDate) => {
  const anchor = parseCivilDate(rule.anchorDate);
  const lower = parseCivilDate(lowerDate);
  if (lowerDate <= rule.anchorDate) return 0;
  let index;
  if (rule.unit === RECURRENCE_UNITS.WEEK) {
    index = Math.ceil((civilToOrdinal(lower) - civilToOrdinal(anchor)) / (rule.interval * 7));
  } else if (rule.unit === RECURRENCE_UNITS.MONTH) {
    const monthDifference = (lower.year - anchor.year) * 12 + lower.month - anchor.month;
    index = Math.max(0, Math.floor(monthDifference / rule.interval));
  } else {
    index = Math.max(0, Math.floor((lower.year - anchor.year) / rule.interval));
  }
  let candidate = getOccurrenceAtIndex(rule, index);
  if (candidate && candidate < lowerDate) {
    index += 1;
    candidate = getOccurrenceAtIndex(rule, index);
  }
  return candidate ? index : null;
};

const maxDate = (...dates) => dates.filter(Boolean).sort().at(-1) || null;
const minDate = (...dates) => dates.filter(Boolean).sort().at(0) || null;
const getRuleLowerBound = (rule, requestedStart) => maxDate(
  rule.anchorDate,
  rule.effectiveFromDate,
  requestedStart
);
const getRuleUpperBound = (rule, requestedEnd = null) => minDate(
  rule.untilDate,
  rule.effectiveUntilDate,
  requestedEnd
);

export const createOccurrenceKey = (seriesId, scheduledDate) => {
  if (!isValidSeriesId(seriesId) || !parseCivilDate(scheduledDate)) {
    throw new RecurrenceDomainError(RECURRENCE_ERROR_CODES.INVALID_OCCURRENCE_IDENTITY);
  }
  return `${seriesId}_${scheduledDate.replace(/-/g, '')}`;
};

export const getNextOccurrenceDate = (rule, afterDate, { inclusive = false } = {}) => {
  assertValidRule(rule);
  if (!parseCivilDate(afterDate) || typeof inclusive !== 'boolean') {
    throw new RecurrenceDomainError(RECURRENCE_ERROR_CODES.INVALID_RANGE);
  }
  const lowerBound = getRuleLowerBound(rule, afterDate);
  let index = getFirstIndexOnOrAfter(rule, lowerBound);
  if (index === null) return null;
  let candidate = getOccurrenceAtIndex(rule, index);
  if (!inclusive && candidate === afterDate) {
    index += 1;
    candidate = getOccurrenceAtIndex(rule, index);
  }
  const upperBound = getRuleUpperBound(rule);
  if (!candidate || (upperBound && candidate > upperBound)) return null;
  return candidate;
};

export const generateRecurrenceOccurrences = (rule, startDate, endDate) => {
  assertValidRule(rule);
  if (!parseCivilDate(startDate) || !parseCivilDate(endDate) || startDate > endDate) {
    throw new RecurrenceDomainError(RECURRENCE_ERROR_CODES.INVALID_RANGE);
  }
  const lowerBound = getRuleLowerBound(rule, startDate);
  const upperBound = getRuleUpperBound(rule, endDate);
  if (!lowerBound || !upperBound || lowerBound > upperBound) return [];
  let index = getFirstIndexOnOrAfter(rule, lowerBound);
  if (index === null) return [];

  const occurrences = [];
  let iterations = 0;
  while (true) {
    const scheduledDate = getOccurrenceAtIndex(rule, index);
    if (!scheduledDate || scheduledDate > upperBound) break;
    if (iterations >= MAX_RECURRENCE_ITERATIONS) {
      throw new RecurrenceDomainError(RECURRENCE_ERROR_CODES.ITERATION_LIMIT_EXCEEDED);
    }
    iterations += 1;
    occurrences.push({
      seriesId: rule.seriesId,
      occurrenceKey: createOccurrenceKey(rule.seriesId, scheduledDate),
      scheduledDate,
    });
    index += 1;
  }
  return occurrences;
};
